import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import { getInitials, showErrorToast } from '../../utils/utils'
import CustomAvatar from '../avatar'
import CustomDialog from '../dialog'
import IconifyIcon from '../icon'
import { useCallback, useEffect, useState } from 'react'
import { getCandidateAssessments } from '../../axios/api/chatbot'
import { CHAT_BOT_ACTION_TYPES } from '../../utils/constant'

const ResendCandidateAssessmentsModal = ({
  open,
  message,
  candidate,
  onClose,
  onConfirmClick
}) => {
  // State
  const [loading, setLoading] = useState(false)
  const [assessments, setAssessments] = useState([])

  // Handlers
  const fetchAssessments = useCallback(async (candidateId, actionType) => {
    setLoading(true)
    const payloadActionType =
      actionType === CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT
        ? 're-enable'
        : actionType === CHAT_BOT_ACTION_TYPES.RESEND_INVITE
          ? 'resend-invite'
          : actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY ||
              actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_NAVIGATE
            ? 'summary'
            : 'update-media-config'
    const response = await getCandidateAssessments(
      candidateId,
      payloadActionType
    )
    setLoading(false)
    if (response?.isError) {
      showErrorToast(response)
      setAssessments([])
    } else {
      setAssessments(response)
    }
  }, [])

  //useEffect
  useEffect(() => {
    if (open) {
      setLoading(true)
      fetchAssessments(candidate?.id, message?.actionType)
    } else {
      setLoading(false)
    }
  }, [open, candidate?.id, message?.actionType, fetchAssessments])

  if (!candidate) return null

  return (
    <CustomDialog
      open={open}
      title="Candidate Assessments"
      handleClose={onClose}
      dialogPaperStyle={{ maxWidth: 600, width: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pb: 3, borderBottom: '1px solid var(--divider)' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <CustomAvatar
              sx={{
                width: 50,
                height: 50,
                fontWeight: 600,
                fontSize: 18
              }}
            >
              {getInitials(candidate.name || '')}
            </CustomAvatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                {candidate.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={0.5}
                mb={0.5}
              >
                <IconifyIcon icon="mdi:email-outline" fontSize="16px" />
                {candidate.email}
              </Typography>
              {candidate.phoneNumber && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <IconifyIcon icon="mdi:phone-outline" fontSize="16px" />
                  {candidate.phoneNumber}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ pt: 3 }}>
          <Typography
            variant="subtitle2"
            color="text.primary"
            fontWeight={600}
            mb={2}
          >
            Associated Assessments
          </Typography>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {assessments?.length > 0 && !loading ? (
            <List
              sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {assessments?.map((assessment) => (
                <ListItem
                  key={assessment.assessmentId}
                  sx={{
                    gap: '16px',
                    border: '1px solid var(--divider)',
                    borderRadius: 2,
                    p: 2,
                    alignItems: 'flex-start'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {assessment.assessmentName}
                        </Typography>
                        <Chip
                          label={
                            assessment.isAssessment ? 'Assessment' : 'Contest'
                          }
                          size="small"
                          sx={{ height: 20, fontSize: '10px', flexShrink: 0 }}
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <IconifyIcon icon="mdi:domain" fontSize="14px" />
                        <Typography variant="caption">
                          Workspace: <strong>{assessment.workspaceName}</strong>
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() =>
                        onConfirmClick(candidate, assessment, message)
                      }
                      sx={{ textTransform: 'none' }}
                      disabled={
                        loading ||
                        (message?.actionType ===
                        CHAT_BOT_ACTION_TYPES.UPDATE_MEDIA_CONFIG
                          ? !assessment?.canPerformAction
                          : false)
                      }
                    >
                      {message?.actionType ===
                      CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT
                        ? 'Re-enable Attempt'
                        : message?.actionType ===
                            CHAT_BOT_ACTION_TYPES.RESEND_INVITE
                          ? 'Resend Invite'
                          : message?.actionType ===
                              CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY
                            ? 'Create Link'
                            : message?.actionType ===
                                CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_NAVIGATE
                              ? 'View Summary'
                              : 'Update Config'}
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              py={3}
            >
              No assessments found for this candidate.
            </Typography>
          )}
        </Box>
      </Box>
    </CustomDialog>
  )
}

export default ResendCandidateAssessmentsModal
